import { forwardRef, SyntheticEvent, useImperativeHandle, useRef } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { ArrowRight } from 'lucide-react';

export interface BlockedUnitTooltipHandle {
  toggle: (e: SyntheticEvent) => void;
}

interface Props {
  /** Send the student to their focused unit. Owned by UnitsPage, which holds the focus + navigate. */
  onGoToFocus: () => void;
}

/**
 * The "ClickDetail" popover shown when a student clicks a blocked unit on the All Units page
 * (US-9, TEAMC-3149). Presentational: UnitsPage renders a single instance and calls `toggle(e)`
 * from each blocked card, so the one panel anchors to whichever card was clicked.
 */
const BlockedUnitTooltip = forwardRef<BlockedUnitTooltipHandle, Props>(({ onGoToFocus }, ref) => {
  const op = useRef<OverlayPanel>(null);

  useImperativeHandle(ref, () => ({ toggle: (e: SyntheticEvent) => op.current?.toggle(e) }), []);

  const handleGoToFocus = () => {
    op.current?.hide();
    onGoToFocus();
  };

  return (
    <OverlayPanel ref={op} pt={{ content: { className: '!tw-p-0' } }}>
      <div className="tw-flex tw-max-w-[278px] tw-flex-col tw-items-center tw-gap-3 tw-rounded-xl tw-bg-[#fefbf9] tw-p-6">
        <p className="tw-m-0 tw-text-center tw-font-atkinson tw-text-lg tw-text-[#393939]">
          Your teacher wants you to <span className="tw-font-bold">focus on another Unit</span>
        </p>
        <button
          type="button"
          onClick={handleGoToFocus}
          className="tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-full tw-border-none tw-bg-transparent tw-px-6 tw-py-3 tw-font-atkinson tw-text-base tw-font-bold tw-text-[#1259fb]"
          aria-label="Take me to my focus unit"
        >
          Take me to my focus
          <ArrowRight className="tw-h-4 tw-w-4" aria-hidden />
        </button>
      </div>
    </OverlayPanel>
  );
});

BlockedUnitTooltip.displayName = 'BlockedUnitTooltip';

export default BlockedUnitTooltip;
